import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as expertInquirySelectors from "@rentiohq/shared-frontend/dist/redux/expertInquiry/expertInquiry.selectors";
import { ITemplate } from "@rentiohq/shared-frontend/dist/redux/template/template.types";
import { ITemplateDocument } from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.types";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IDocumentPackage } from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { IRegistration } from "@rentiohq/shared-frontend/dist/types/registration.types";
import { IRentDeposit } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import { useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { EAdministrationItems } from "../AdministrationItems/AdministrationItems.types";
import {
  getBrusselsRegistrationMessage,
  getDocumentPackageMessage,
  getInsuranceMessage,
  getRentioMoveServiceMessage,
  getPaymentOrderMessage,
  getPropertyInspectionReportMessage,
  getRegistrationMessage,
  getRentDepositMessage,
  getTemplateDocumentContractMessage,
} from "./../../ContractCard.utils";

export const useGetEntryChecklistItems = (params: {
  contract: IContract;
  property: IProperty;
  rentDeposits?: IRentDeposit[];
  paymentOrdersV1?: IPaymentOrder[];
  paymentOrdersV2?: IPaymentOrder[];
  documentPackages?: IDocumentPackage[];
  paymentOrdersCount?: number;
  templates?: ITemplate[];
  contractTemplateDocuments?: ITemplateDocument[];
  registration?: IRegistration;
  isFetchingTemplates?: boolean;
  isFetchingContractTemplateDocuments?: boolean;
  isFetchingDocumentPackages?: boolean;
  isFetchingRentDeposits?: boolean;
  isFetchingPaymentOrdersCount?: boolean;
  // Callbacks
  onCreateContractFromTemplateDocument: () => void;
  onShowDocument: (documentId: string) => void;
  onShowTemplateDocument: (templateDocumentId: string) => void;
  onShowDocumentPackage: (documentPackageId: string) => void;
  onShowRentDeposit: (rentDepositId: number) => void;
  onOrderPropertyInspectionReport: () => void;
  onRequestInsurance: () => void;
  onShowInsurance: (insuranceContractId: string) => void;
  onCreateRentioMoveRequest: () => void;
  onShowMoveRequest: (moveRequestId: number) => void;
  onShowRentRegistration: (registrationId: number) => void;

  // Config
  shouldFetchData: boolean;
}) => {
  const {
    contract,
    property,
    rentDeposits,
    documentPackages,
    templates,
    paymentOrdersCount,
    contractTemplateDocuments,
    registration,
    isFetchingTemplates,
    isFetchingContractTemplateDocuments,
    isFetchingDocumentPackages,
    isFetchingRentDeposits,
    isFetchingPaymentOrdersCount,
    onCreateContractFromTemplateDocument,
    onShowDocument,
    onShowTemplateDocument,
    onShowDocumentPackage,
    onShowRentDeposit,
    onOrderPropertyInspectionReport,
    onRequestInsurance,
    onShowInsurance,
    onCreateRentioMoveRequest,
    onShowMoveRequest,
    onShowRentRegistration,
  } = params;
  const { internalModeEnabled } = useInternalMode();
  const { isBroker, broker } = authHooks.useSelf();

  const { update: updateContract } = contractHooks.useUpdate({
    id: contract.id,
  });

  // Property inspection report
  const partnerships = useSelector((state: IRootStore) =>
    expertInquirySelectors.partnerships(state),
  );

  // Broker features
  const hasInsuranceForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Insurance,
  );
  const hasRentioMoveServiceForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.RentioMove,
  );

  // Build checklist items
  const checklistItems = {
    [EAdministrationItems.Contract]: getTemplateDocumentContractMessage({
      property,
      contract,

      // Callbacks
      onCreateContractFromTemplateDocument,
      onShowDocument,
      onShowTemplateDocument,

      // Update
      updateContract,

      // Config
      isBroker,
      templates,
      isFetchingTemplates: isFetchingTemplates || false,
      contractTemplateDocuments,
      isFetchingContractTemplateDocuments:
        isFetchingContractTemplateDocuments || false,
    }),
    [EAdministrationItems.Sign]: getDocumentPackageMessage({
      property,
      contract,

      // Callbacks
      onShowDocumentPackage,

      // Actions
      updateContract,

      // Config
      documentPackage: documentPackages?.[0],
      isFetchingDocumentPackage: isFetchingDocumentPackages || false,
    }),
    [EAdministrationItems.Deposit]: getRentDepositMessage({
      property,
      contract,

      // Callbacks
      onShowRentDeposit,

      // Actions
      updateContract,

      // Config
      rentDeposit: rentDeposits?.[0],
      isFetchingRentDeposit: isFetchingRentDeposits || false,
    }),
    [EAdministrationItems.Rent]: getPaymentOrderMessage({
      property,
      contract,

      // Actions
      updateContract,

      // Config
      isBroker,
      paymentOrdersCount,
      isFetchingPaymentOrdersCount: isFetchingPaymentOrdersCount || false,
    }),
    [EAdministrationItems.PropertyInspectionReport]:
      getPropertyInspectionReportMessage({
        property,
        contract,
        isBroker,
        broker,

        // Callbacks
        onOrderPropertyInspectionReport,

        // Actions
        updateContract,

        // Config
        partnerships,
      }),
    [EAdministrationItems.FireInsurance]: getInsuranceMessage({
      property,
      contract,

      // Callbacks
      onShowInsurance,
      onRequestInsurance,

      // Actions
      updateContract,

      // Config
      isBroker,
      hasInsuranceForBroker,
      internalModeEnabled,
    }),
    ...(hasRentioMoveServiceForBroker || Boolean(contract.rentioMoveStatus)
      ? {
          [EAdministrationItems.Energy]: getRentioMoveServiceMessage({
            property,
            contract,

            // Callbacks
            onCreateRentioMoveRequest,
            onShowMoveRequest,

            // Actions
            updateContract,

            // Config
            isBroker,
            hasRentioMoveServiceForBroker,
            internalModeEnabled,
          }),
        }
      : {}),
    [EAdministrationItems.Register]: getRegistrationMessage({
      property,
      contract,
      registration,

      // Callbacks
      onShowRentRegistration,

      // Actions
      updateContract,
    }),
    [EAdministrationItems.BrusselsRegistration]: getBrusselsRegistrationMessage(
      {
        property,
        contract,
        registration,

        // Callbacks
        onShowRentRegistration,

        // Actions
        updateContract,
      },
    ),
  };

  return Object.fromEntries(
    Object.entries(checklistItems).filter(([_, value]) => Boolean(value)),
  );
};
